/**
 * Separate — detach selected geometry into a new SceneObject.
 *
 * Face path: extracts all faces whose vertices are fully selected.
 * Wire/loose path: when no complete faces are selected, extracts wire edges
 * whose both endpoints are selected, plus any loose (edgeless) selected
 * vertices. Matches Blender's P behaviour in vertex/edge mode on wire meshes.
 */

import {
  deleteVertices,
  VERTEX_REMOVED,
  type VertexProvenance,
} from "../../wasm/editOps.ts";
import {
  recomputeLoopNormals,
  resolveAutoSmoothAngle,
} from "../../wasm/normals.ts";
import {
  identityProvenance,
  inheritPerVertexAttributes,
  inheritTopologyAttributes,
  provenanceFromCompactRemap,
} from "../provenance.ts";
import {
  extractSubTopology,
  identityRemap,
  rebuildTopologyEdgesPreservingWire,
  topologyFaceVerts,
  triangulateTopologyForRendering,
} from "../topology.ts";
import type {
  LoopNormals,
  PolygonTopology,
  RenderTriangleIndices,
  SceneObject,
} from "../types.ts";
import { createSceneObjectId, EDGE_WIRE } from "../types.ts";
import { unwrapBoxProject } from "../uvUnwrap.ts";

/**
 * Build a `VertexProvenance` from an extract-remap map (old vert → new vert,
 * absent = removed). The resulting provenance has each surviving new vert
 * with the source old vert as its single parent.
 */
function provenanceFromExtractMap(
  extractRemap: Map<number, number>,
  oldVertCount: number,
): VertexProvenance {
  const remap = new Int32Array(oldVertCount).fill(VERTEX_REMOVED);
  for (const [oldIdx, newIdx] of extractRemap) {
    remap[oldIdx] = newIdx;
  }
  return provenanceFromCompactRemap(remap);
}

export interface SeparateResult {
  /** The original mesh with selected faces removed. Per-vertex attributes
   *  (vertex groups, skin radii, sculpt data) are already reconciled to the
   *  new vertex layout via the delete provenance.
   *
   *  `provenance` maps source-object vertex indices forward to the compacted
   *  remaining layout — exposed so the store wrapper can route the remaining
   *  piece through `applyResult`, which re-runs normal/UV/attribute
   *  reconciliation through the same ceremony every other topology op uses
   *  (including DEV-only validation and generator-ref stripping). */
  remaining: {
    vertices: Float32Array;
    indices: RenderTriangleIndices;
    loopNormals: LoopNormals;
    topology: PolygonTopology;
    uvs?: Float32Array;
    /** Per-vertex attribute fields inherited (and reconciled) from the
     *  source object via the extract / delete provenance. */
    vertexGroups?: SceneObject["vertexGroups"];
    skinRadii?: Float32Array;
    baseVertices?: Float32Array;
    provenance: VertexProvenance;
  };
  /** The extracted geometry as a new SceneObject. */
  separated: SceneObject;
}

/**
 * Separate selected faces from the mesh into a new SceneObject.
 *
 * @param obj           The source SceneObject being edited.
 * @param selectedVerts The set of selected vertex indices.
 * @returns null if no complete faces are selected.
 */
export function separateSelection(
  obj: SceneObject,
  selectedVerts: ReadonlySet<number>,
  explicitFaces?: ReadonlySet<number>,
): SeparateResult | null {
  const { vertices, indices, topology } = obj;
  if (!topology) return null;

  const { offsets, corners } = topology;
  const faceCount = offsets.length - 1;

  // 1. Find faces that are fully selected. Use explicit face set when
  //    available to avoid shared-vertex inference errors.
  const selectedFaceIndices = new Set<number>();
  const selectedFaceVerts = new Set<number>();
  if (explicitFaces && explicitFaces.size > 0) {
    for (const f of explicitFaces) {
      selectedFaceIndices.add(f);
      const fv = topologyFaceVerts(topology, f);
      for (const v of fv) selectedFaceVerts.add(v);
    }
  } else {
    for (let f = 0; f < faceCount; f++) {
      const fv = topologyFaceVerts(topology, f);
      if (fv.every((v) => selectedVerts.has(v))) {
        selectedFaceIndices.add(f);
        for (const v of fv) selectedFaceVerts.add(v);
      }
    }
  }
  if (selectedFaceIndices.size === 0) {
    // No complete faces enclosed — fall back to wire-edge / loose-vertex separation.
    return separateWireOrLooseVertices(obj, selectedVerts);
  }

  // 2. Build remap for extracted geometry: old index → new compact index
  const extractRemap = new Map<number, number>();
  let newIdx = 0;
  for (const v of selectedFaceVerts) {
    extractRemap.set(v, newIdx++);
  }

  // 3. Compact vertex array for the extracted piece. Per-vertex attributes
  //    flow through `inheritPerVertexAttributes` later; per-corner data
  //    (loop normals, UVs) is recomputed from scratch against the new
  //    topology — there's no meaningful way to remap them from the source
  //    object's per-corner layout.
  const extractedVerts = new Float32Array(extractRemap.size * 3);
  for (const [oldIdx, ni] of extractRemap) {
    extractedVerts[ni * 3] = vertices[oldIdx * 3];
    extractedVerts[ni * 3 + 1] = vertices[oldIdx * 3 + 1];
    extractedVerts[ni * 3 + 2] = vertices[oldIdx * 3 + 2];
  }

  // 4. Extract topology for the separated piece, recompute per-corner
  //    loop normals against it, and box-project UVs in per-corner mode.
  //    Per-corner UVs require both a corner-indexed normal array AND the
  //    topology, so the normals must be computed BEFORE the UVs.
  const extractedTopology = extractSubTopology(
    topology,
    selectedFaceVerts,
    extractRemap,
  );
  const extractedIndices = triangulateTopologyForRendering(extractedTopology);
  const autoSmooth = resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle);
  const sepLoopNormals = recomputeLoopNormals(
    extractedVerts,
    extractedTopology,
    autoSmooth,
  );
  const extractedUVs = unwrapBoxProject(
    extractedVerts,
    sepLoopNormals,
    extractedTopology,
  );

  // 6. Remove selected faces from original via deleteVertices
  //    Vertices only belonging to selected faces will be orphaned and removed.
  //    We delete vertices that appear ONLY in selected faces.
  const remainingFaceVerts = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    if (!selectedFaceIndices.has(f)) {
      const fv = topologyFaceVerts(topology, f);
      for (const v of fv) remainingFaceVerts.add(v);
    }
  }
  // Also include vertices from wire edges that aren't in selected faces
  for (let i = 0; i < topology.edges.length; i++) {
    remainingFaceVerts.add(topology.edges[i]);
  }

  const vertsToDelete = new Set<number>();
  for (const v of selectedFaceVerts) {
    if (!remainingFaceVerts.has(v)) vertsToDelete.add(v);
  }

  // Build remaining topology by removing selected faces, then delete orphaned vertices
  const remainingCorners: number[] = [];
  const remainingOffsets: number[] = [0];
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    const fv: number[] = [];
    for (let j = 0; j < size; j++) fv.push(corners[start + j]);
    if (!selectedFaceIndices.has(f)) {
      for (const v of fv) remainingCorners.push(v);
      remainingOffsets.push(remainingCorners.length);
    }
  }
  // Preserve wire edges + per-edge attributes on the remaining mesh;
  // vertex indices are unchanged until `deleteVertices` below, which runs
  // its own compaction.
  const remainingTopology = rebuildTopologyEdgesPreservingWire(
    new Uint32Array(remainingOffsets),
    new Uint32Array(remainingCorners),
    topology,
    identityRemap,
  );

  const remaining = deleteVertices(
    vertices,
    indices,
    remainingTopology,
    vertsToDelete,
  );
  const remainingTopologyWithAttrs = inheritTopologyAttributes(
    remainingTopology,
    remaining.topology,
    remaining.provenance,
  );
  // The deleteVertices provenance maps the source object's old vert space
  // forward to the remaining object's compacted layout. Reconcile per-vertex
  // attributes through it so the remaining piece keeps its skin radii,
  // sculpt data, and any vertex groups whose indices survived the cull.
  const remainingAttrs = inheritPerVertexAttributes(
    obj,
    {
      ...obj,
      vertices: remaining.vertices,
      topology: remainingTopologyWithAttrs,
      indices: remaining.indices,
    } as SceneObject,
    remaining.provenance,
  );
  const remLoopNormals = recomputeLoopNormals(
    remaining.vertices,
    remainingTopologyWithAttrs,
    autoSmooth,
  );

  // 7. Build new SceneObject for the separated piece
  const nameParts = obj.name.split(".");
  const baseName = nameParts[0];

  const separatedScaffold: SceneObject = {
    id: createSceneObjectId(),
    name: `${baseName}.sep`,
    objectKind: obj.objectKind,
    position: [...obj.position] as [number, number, number],
    rotation: [...obj.rotation] as [number, number, number],
    scale: [...obj.scale] as [number, number, number],
    vertices: extractedVerts,
    indices: extractedIndices,
    loopNormals: sepLoopNormals,
    topology: extractedTopology,
    materialSlots: obj.materialSlots.map((s) => ({ ...s })),
    shading: obj.shading,
    uvs: extractedUVs,
  };

  // Inherit source's per-vertex attributes (vertex groups, skin radii, sculpt
  // data) onto the extracted subset via a synthesised provenance from the
  // extract-remap map.
  const separateProv = provenanceFromExtractMap(
    extractRemap,
    obj.vertices.length / 3,
  );
  const separated = inheritPerVertexAttributes(
    obj,
    separatedScaffold,
    separateProv,
  );

  return {
    remaining: {
      vertices: remaining.vertices,
      topology: remainingTopologyWithAttrs,
      indices: triangulateTopologyForRendering(remainingTopologyWithAttrs),
      loopNormals: remLoopNormals,
      uvs: unwrapBoxProject(
        remaining.vertices,
        remLoopNormals,
        remainingTopologyWithAttrs,
      ),
      vertexGroups: remainingAttrs.vertexGroups,
      skinRadii: remainingAttrs.skinRadii,
      baseVertices: remainingAttrs.baseVertices,
      provenance: remaining.provenance,
    },
    separated,
  };
}

/**
 * Separate wire edges and loose vertices when no complete face is enclosed
 * by the selection. Mirrors Blender's P behaviour in vertex/edge mode on
 * wire-only or mixed-topology meshes.
 *
 * Extracted vertices:
 *  - Both endpoints of a wire edge (EDGE_WIRE) that are selected.
 *  - Loose vertices selected but not referenced by any edge or face.
 *
 * The new object receives a wire-only topology; the originating mesh has the
 * extracted vertices deleted (vertices shared with faces are left in place).
 */
function separateWireOrLooseVertices(
  obj: SceneObject,
  selectedVerts: ReadonlySet<number>,
): SeparateResult | null {
  const { vertices, indices, topology } = obj;
  if (!topology) return null;

  const { edges, edgeFlags } = topology;
  const edgeCount = edges.length / 2;

  // Build face-corner vertex set to distinguish wire/loose from face-bound.
  const faceVerts = new Set<number>();
  for (let i = 0; i < topology.corners.length; i++)
    faceVerts.add(topology.corners[i]);

  // Vertices incident to selected wire edges.
  const extractVerts = new Set<number>();
  for (let i = 0; i < edgeCount; i++) {
    if (!(edgeFlags[i] & EDGE_WIRE)) continue;
    const v0 = edges[i * 2],
      v1 = edges[i * 2 + 1];
    if (selectedVerts.has(v0) && selectedVerts.has(v1)) {
      extractVerts.add(v0);
      extractVerts.add(v1);
    }
  }

  // Loose vertices: selected but not referenced by any edge or face.
  const allEdgeVerts = new Set<number>();
  for (let i = 0; i < edges.length; i++) allEdgeVerts.add(edges[i]);
  for (const v of selectedVerts) {
    if (!faceVerts.has(v) && !allEdgeVerts.has(v)) extractVerts.add(v);
  }

  if (extractVerts.size === 0) return null;

  // Build compact remap (sorted for determinism).
  const sorted = [...extractVerts].sort((a, b) => a - b);
  const extractRemap = new Map<number, number>();
  for (let i = 0; i < sorted.length; i++) extractRemap.set(sorted[i], i);

  // Compact vertex array.
  const extractedVerts = new Float32Array(extractRemap.size * 3);
  for (const [oldIdx, ni] of extractRemap) {
    extractedVerts[ni * 3] = vertices[oldIdx * 3];
    extractedVerts[ni * 3 + 1] = vertices[oldIdx * 3 + 1];
    extractedVerts[ni * 3 + 2] = vertices[oldIdx * 3 + 2];
  }

  // Wire-only topology for the extracted piece: no faces, wire edges and
  // their per-edge attributes preserved via rebuildTopologyEdgesPreservingWire
  // (maps -1 for vertices outside the set).
  const extractedTopology = rebuildTopologyEdgesPreservingWire(
    new Uint32Array([0]),
    new Uint32Array(0),
    topology,
    (v) => (extractRemap.has(v) ? extractRemap.get(v)! : -1),
  );
  const extractedIndices = triangulateTopologyForRendering(extractedTopology);

  const autoSmooth = resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle);
  const sepLoopNormals = recomputeLoopNormals(
    extractedVerts,
    extractedTopology,
    autoSmooth,
  );
  // No face corners → per-corner UV array is empty; pass zero normals placeholder.
  const extractedUVs = unwrapBoxProject(
    extractedVerts,
    new Float32Array(extractedVerts.length),
    extractedTopology,
  );

  // Delete extracted vertices from original, skipping any that are also face-bound
  // (those stay in the original as part of their faces).
  const vertsToDelete = new Set<number>();
  for (const v of extractVerts) {
    if (!faceVerts.has(v)) vertsToDelete.add(v);
  }

  let remaining: SeparateResult["remaining"];
  if (vertsToDelete.size > 0) {
    const del = deleteVertices(vertices, indices, topology, vertsToDelete);
    const remainingTopology = inheritTopologyAttributes(
      topology,
      del.topology,
      del.provenance,
    );
    const remLoopNormals = recomputeLoopNormals(
      del.vertices,
      remainingTopology,
      autoSmooth,
    );
    const remainingAttrs = inheritPerVertexAttributes(
      obj,
      {
        ...obj,
        vertices: del.vertices,
        topology: remainingTopology,
        indices: del.indices,
      } as SceneObject,
      del.provenance,
    );
    remaining = {
      vertices: del.vertices,
      topology: remainingTopology,
      indices: triangulateTopologyForRendering(remainingTopology),
      loopNormals: remLoopNormals,
      uvs: unwrapBoxProject(del.vertices, remLoopNormals, remainingTopology),
      vertexGroups: remainingAttrs.vertexGroups,
      skinRadii: remainingAttrs.skinRadii,
      baseVertices: remainingAttrs.baseVertices,
      provenance: del.provenance,
    };
  } else {
    // All extracted vertices are shared with faces; original mesh unchanged.
    // Per-vertex attributes pass through identically.
    const remLoopNormals = recomputeLoopNormals(vertices, topology, autoSmooth);
    remaining = {
      vertices,
      indices: triangulateTopologyForRendering(topology),
      topology,
      loopNormals: remLoopNormals,
      uvs: unwrapBoxProject(vertices, remLoopNormals, topology),
      vertexGroups: obj.vertexGroups,
      skinRadii: obj.skinRadii,
      baseVertices: obj.baseVertices,
      provenance: identityProvenance(vertices.length / 3),
    };
  }

  const nameParts = obj.name.split(".");
  const baseName = nameParts[0];
  const separatedScaffold: SceneObject = {
    id: createSceneObjectId(),
    name: `${baseName}.sep`,
    objectKind: obj.objectKind,
    position: [...obj.position] as [number, number, number],
    rotation: [...obj.rotation] as [number, number, number],
    scale: [...obj.scale] as [number, number, number],
    vertices: extractedVerts,
    indices: extractedIndices,
    loopNormals: sepLoopNormals,
    topology: extractedTopology,
    materialSlots: obj.materialSlots.map((s) => ({ ...s })),
    shading: obj.shading,
    uvs: extractedUVs,
  };

  const separateProv = provenanceFromExtractMap(
    extractRemap,
    vertices.length / 3,
  );
  const separated = inheritPerVertexAttributes(
    obj,
    separatedScaffold,
    separateProv,
  );

  return { remaining, separated };
}
